import React, { useMemo } from "react";

import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  Tooltip,
  useTheme,
  Theme,
} from "@mui/material";
import Typography, { TypographyProps } from "@common/components/Typography";
import { useTranslation } from "react-i18next";
import {
  responsiveFontSize,
  TypoSize,
} from "@common/components/Typography/style";
import deleteBtn from "./DeleteButton";
export const DeleteButton = deleteBtn;
import TicketPriority from "./TicketPriority";

export type Variants = "text" | "contained" | "outlined";

export type Colors =
  | "success"
  | "error"
  | "warning"
  | "greyed"
  | "meeting"
  | "transparent"
  | "white"
  | "primary"
  | "loading"
  | "meeting"
  | "disabled"
  | "Critical"
  | "Major"
  | "Minor"
  | "Informational"
  | "default";

export const PriorityButtons = TicketPriority;

const getColors = (theme: Theme, variant: Variants, color?: Colors) => {
  const { palette } = theme;
  const build = (
    backgroundColor: string,
    color: string,
    borderColor?: string,
    disabledColor?: string
  ) => ({
    backgroundColor,
    color,
    border: `1px solid ${borderColor ?? backgroundColor}`,
    "&:hover": { backgroundColor },
    "& .MuiCircularProgress-root": {
      color,
    },
    "&:disabled": {
      color: disabledColor ?? color,
    },
  });
  const buildText = (color: string) => ({
    color,
    backgroundColor: "transparent",
    border: "none",
    "& .MuiCircularProgress-root": {
      color,
    },
  });
  const buildOutlined = (color: string, textColor?: string) => ({
    color: textColor ?? color,
    border: `1px solid ${color}`,
    backgroundColor: "transparent",
    "& .MuiCircularProgress-root": {
      color,
    },
  });

  switch (color) {
    case "success":
      if (variant === "outlined") return buildOutlined(palette.success.main);
      if (variant === "text") return buildText(palette.success.main);
      return build(palette.success.main, palette.common.black);
    case "warning":
      if (variant === "outlined") return buildOutlined(palette.warning.main);
      if (variant === "text") return buildText(palette.warning.main);
      return build(palette.warning.main, palette.common.white);
    case "error":
      if (variant === "outlined") return buildOutlined(palette.error.main);
      if (variant === "text") return buildText(palette.error.main);
      return build(palette.error.main, palette.common.white);
    case "greyed":
      if (variant === "outlined")
        return buildOutlined(
          palette.common.grey.g828282,
          palette.common.grey.gE6E6E6
        );
      if (variant === "text") return buildText(palette.common.grey.light);
      return build(palette.common.grey.light, palette.common.black);
    case "meeting":
      if (variant === "outlined")
        return buildOutlined(palette.meeting.background);
      if (variant === "text") return buildText(palette.meeting.background);
      return build(palette.meeting.background, palette.common.white);
    case "transparent":
      return build(
        "transparent",
        palette.common.grey.light,
        palette.common.grey.light
      );
    case "disabled":
      if (variant === "contained")
        return build(palette.common.grey.g474a53, palette.common.grey.g828282);
      if (variant === "outlined")
        return buildOutlined(palette.common.grey.dark);
      return buildText(palette.common.grey.dark);
    case "primary":
      if (variant === "outlined") return buildOutlined(palette.primary.main);
      if (variant === "text") return buildText(palette.primary.main);
      return build(
        palette.primary.main,
        palette.common.black,
        undefined,
        palette.common.black
      );
    case "white":
      if (variant === "outlined") return buildOutlined(palette.common.white);
      if (variant === "text") return buildText(palette.common.white);
      return build(
        palette.common.white,
        palette.common.black,
        undefined,
        palette.common.grey.light
      );
    case "Critical":
    case "Major":
    case "Minor":
    case "Informational":
      if (variant === "outlined")
        return buildOutlined(palette.ticket[color].main);
      return build(palette.ticket[color].main, palette.common.white);
    case "default":
    default:
      if (variant === "text") return buildText(palette.common.white);
      return build("transparent", palette.common.white, palette.common.white);
  }
};

export interface ButtonProps {
  color?: Colors;
  activeColor?: Colors;
  active?: boolean;
  chip?: boolean;
  weighted?: boolean;
  ignoreLocalization?: boolean;
  size?: TypoSize;
  variant?: Variants;
  loading?: boolean;
  tooltip?: string;
  disableInteractiveTooltip?: boolean;
  alignLabel?: "left";
  TypographyProps?: TypographyProps;
  ["data-testid"]?: string;
}

export type UnionType = Omit<MuiButtonProps, "color" | "size" | "variant"> &
  ButtonProps;

export type ColorSchemes = Colors;

type WrapperProps = {
  showTooltip: boolean;
  title: string;
  children: React.ReactElement<any, any>;
  disableInteractiveTooltip?: boolean;
};

const Wrapper = ({
  children,
  showTooltip,
  title,
  disableInteractiveTooltip,
}: WrapperProps): React.JSX.Element => {
  return showTooltip ? (
    <Tooltip title={title} arrow disableInteractive={disableInteractiveTooltip}>
      {children}
    </Tooltip>
  ) : (
    children
  );
};

const Button = ({
  ref,
  ...props
}: UnionType & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const {
    color,
    activeColor,
    active = false,
    chip = false,
    weighted = false,
    disabled = false,
    ignoreLocalization = false,
    tooltip,
    disableInteractiveTooltip,
    className = "",
    children = "",
    loading = false,
    size = "small",
    variant = "contained",
    alignLabel,
    sx,
    TypographyProps,
    ...other
  } = props;

  const chosenColor = disabled ? "disabled" : active ? activeColor : color;
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = useMemo(
    () => getColors(theme, variant, chosenColor),
    [theme, variant, chosenColor]
  );

  const computedSize = useMemo(() => responsiveFontSize(theme, size), [size]);
  const mainStyle = {
    height: "2em",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    "& .MuiCircularProgress-root": {
      width: "0.75em !important",
      height: "0.75em !important",
    },
  };

  return (
    <MuiButton
      {...other}
      ref={ref}
      disabled={disabled || loading}
      className={`${className}`}
      sx={[
        mainStyle,
        { borderRadius: chip ? "2em" : "8px" },
        colors,
        computedSize,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      startIcon={loading ? <CircularProgress /> : props.startIcon}
    >
      <Wrapper
        showTooltip={tooltip !== undefined}
        title={tooltip ?? ""}
        disableInteractiveTooltip={disableInteractiveTooltip}
      >
        <Typography
          variant="caption"
          size={size}
          sx={{
            fontWeight: weighted ? 500 : 400,
            maxWidth: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            ...(alignLabel && {
              position: "absolute",
              left: (theme) =>
                alignLabel === "left" ? theme.spacing(1) : "unset",
            }),
            ...TypographyProps,
          }}
        >
          {ignoreLocalization || typeof children !== "string"
            ? children
            : t(children)}
        </Typography>
      </Wrapper>
    </MuiButton>
  );
};

export default Button;
